import React, { useState } from 'react';
import { X } from 'lucide-react';

const legalTexts = {
  avisoLegal: {
    title: 'Aviso Legal',
    content: `DATOS IDENTIFICATIVOS DEL TITULAR
En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico, le informamos que el titular de este sitio web es Restaurante Sanatorio, con domicilio en Plaza Mayor 14, 37500 Ciudad Rodrigo (Salamanca), España. Teléfono de contacto: 923 46 10 54.

CONDICIONES DE USO
El acceso y uso de este sitio web atribuye la condición de usuario e implica la aceptación plena de todas las condiciones incluidas en este Aviso Legal. El usuario se compromete a hacer un uso adecuado de los contenidos y servicios que se ofrecen y a no emplearlos para realizar actividades ilícitas o contrarias a las buenas costumbres y al orden público.

PROPIEDAD INTELECTUAL E INDUSTRIAL
Todos los contenidos del sitio web, incluyendo textos, fotografías, gráficos, imágenes, iconos, tecnología, software, así como su diseño gráfico y código fuente, constituyen una obra propiedad de Restaurante Sanatorio y están protegidos por los derechos de Propiedad Intelectual e Industrial. Queda prohibida su reproducción, distribución, comunicación pública y transformación, salvo autorización expresa de su titular.

EXCLUSIÓN DE RESPONSABILIDAD
Restaurante Sanatorio no se hace responsable de los daños e inconvenientes de todo tipo que pudieran derivarse de la utilización de los servicios del Portal ni de la imposibilidad de uso de los mismos.

LEGISLACIÓN APLICABLE
Las presentes condiciones se rigen por la legislación española. Las partes se someten en caso de controversia a los Juzgados y Tribunales de Salamanca.`
  },
  privacidad: {
    title: 'Política de Privacidad',
    content: `RESPONSABLE DEL TRATAMIENTO
Restaurante Sanatorio, Plaza Mayor 14, 37500 Ciudad Rodrigo (Salamanca). Teléfono: 923 46 10 54.

FINALIDAD DEL TRATAMIENTO
Los datos personales que nos facilite serán tratados con las siguientes finalidades:
• Gestión de reservas y comunicación con clientes.
• Envío de información sobre nuestra oferta gastronómica, únicamente si usted lo solicita expresamente.
• Atención y resolución de consultas y reclamaciones.

BASE JURÍDICA
El tratamiento de sus datos está legitimado por el consentimiento del interesado, la ejecución de un contrato en el que el interesado es parte, y el cumplimiento de obligaciones legales.

CONSERVACIÓN DE DATOS
Los datos se conservarán durante el tiempo necesario para cumplir con la finalidad para la que se recabaron y para determinar las posibles responsabilidades que se pudieran derivar de dicha finalidad.

DERECHOS DEL INTERESADO
Puede ejercer sus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición ante el responsable del tratamiento. Para ello, diríjase a Plaza Mayor 14, 37500 Ciudad Rodrigo o mediante correo electrónico al establecimiento, acreditando su identidad.

También tiene derecho a interponer una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es) si considera que el tratamiento de sus datos no es adecuado.`
  },
  cookies: {
    title: 'Política de Cookies',
    content: `¿QUÉ SON LAS COOKIES?
Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Se utilizan ampliamente para hacer que los sitios web funcionen de manera más eficiente y para proporcionar información a los propietarios del sitio.

COOKIES QUE UTILIZAMOS
Este sitio web puede utilizar los siguientes tipos de cookies:

• Cookies técnicas (necesarias): Son imprescindibles para el correcto funcionamiento del sitio. Permiten navegar por la página y utilizar las opciones o servicios que hay en ella. No requieren su consentimiento.

• Cookies de preferencias: Permiten recordar información que cambia el aspecto o el comportamiento del sitio web, como la preferencia de idioma seleccionada.

• Cookies analíticas: Nos permiten cuantificar el número de usuarios y analizar el uso del sitio para mejorar nuestros servicios. Los datos recogidos son estadísticos y anónimos.

GESTIÓN DE COOKIES
Puede permitir, bloquear o eliminar las cookies instaladas en su dispositivo mediante la configuración de las opciones del navegador instalado en su dispositivo. En caso de que no permita la instalación de cookies en su navegador es posible que no pueda acceder a algunos servicios y que su experiencia en el sitio web sea menor.

Para más información sobre cómo gestionar las cookies, consulte la ayuda de su navegador o visite la página de la Agencia Española de Protección de Datos (www.aepd.es).`
  }
};

type LegalKey = keyof typeof legalTexts;

const Footer: React.FC = () => {
  const [openSection, setOpenSection] = useState<LegalKey | null>(null);

  const toggle = (key: LegalKey) => {
    setOpenSection(key);
  };

  const closePopup = () => {
    setOpenSection(null);
  };

  return (
    <>
      <footer className="bg-earth-dark text-cream-organic mt-4 border-t-4 border-burgundy">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-10 text-center md:text-left">
          {/* Column 1 - Restaurant info */}
          <div className="space-y-4">
            <p className="font-serif text-2xl text-burgundy-light text-white font-bold">Restaurante Sanatorio</p>
            <div className="text-sm opacity-80 space-y-1">
              <p>Plaza Mayor 14, Ciudad Rodrigo</p>
              <p>37500 Salamanca, España</p>
              <p className="pt-1">
                <a href="tel:+34923461054" className="hover:text-burgundy-light transition-colors">
                  923 46 10 54
                </a>
              </p>
            </div>
          </div>

          {/* Column 2 - Legal */}
          <div className="space-y-4">
            <p className="font-sans font-bold uppercase tracking-widest text-xs text-burgundy-light/60">Información Legal</p>
            <div className="flex flex-col gap-2 text-xs">
              {(Object.keys(legalTexts) as LegalKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => toggle(key)}
                  className="flex items-center justify-center md:justify-start gap-1 transition-colors opacity-60 hover:text-burgundy-light hover:opacity-100"
                >
                  <span>{legalTexts[key].title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Column 3 - Development */}
          <div className="space-y-4">
            <p className="font-sans font-bold uppercase tracking-widest text-xs text-burgundy-light/60">Desarrollo</p>
            <p className="text-xs opacity-60">
              Diseño y Desarrollo por{' '}
              <a
                href="https://spmundi.es"
                target="_blank"
                rel="noopener noreferrer"
                className="text-burgundy-light hover:underline"
              >
                SPM Servicios Publicitarios Mundi
              </a>
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 pb-8 border-t border-cream-organic/10 text-center pt-6">
          <p className="text-[10px] opacity-40 uppercase tracking-[0.2em]">
            &copy; {new Date().getFullYear()} Restaurante Sanatorio. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Legal Popup Overlay */}
      {openSection && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={closePopup}>
          <div
            className="bg-cream-organic w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl shadow-2xl relative border border-earth-light/20 p-8"
            onClick={(e) => e.stopPropagation()} /* Prevent click inside modal from closing it */
          >
            <button
              onClick={closePopup}
              className="absolute top-6 right-6 p-2 rounded-full bg-earth-light/10 hover:bg-earth-light/20 text-earth-dark transition-colors"
            >
              <X size={20} />
            </button>
            <h3 className="font-serif text-3xl text-burgundy mb-6 pr-8">{legalTexts[openSection].title}</h3>
            <div className="text-sm text-earth-dark/80 leading-relaxed whitespace-pre-line space-y-4 font-sans">
              {legalTexts[openSection].content}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;